import { useEffect, useState } from 'react'

import {
  IconCircleCheck,
  IconCircleCheckSolid,
  IconPlusLight,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
  Tooltip,
  UpsellCTA
} from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useApiHubPlan } from '~/portal/hooks/useApiHubPlan'
import { TemplateItem } from '~/portal/types/template'
import { useShubOrgInfo } from '~/shared/hooks/useShubOrgInfo'

interface SelectTemplateProps {
  initialSelectedId?: string,
  templates: TemplateItem[],
  setFormValue: (value?: string) => void,
}

export const SelectTemplate = ({ initialSelectedId, templates, setFormValue }: SelectTemplateProps) => {
  const { t } = useTranslation()
  const defaultTemplate = templates.find(template => template.default)
  const initialSelectTemplate = templates.find(template => template.id === initialSelectedId)

  const initialSelectValue = initialSelectTemplate || defaultTemplate
  const [template, setTemplate] = useState<SelectItem | undefined>(initialSelectValue)

  const [selectEnabled, setSelectEnabled] = useState(Boolean(initialSelectValue))

  const navigate = useNavigate()

  const { organizationRole } = useShubOrgInfo()

  const {
    templatesEnabled,
    getUpgradeLabel,
    getUpgradeUrl,
  } = useApiHubPlan()

  useEffect(() => {
    setFormValue(initialSelectValue?.id)
  }, [])

  const onClickEmptyProduct = () => {
    setSelectEnabled(false)
    setFormValue(undefined)
  }

  const onSelect = (option: SelectItem) => {
    setTemplate(option)
    setFormValue(option.id)
  }

  const onClickTemplate = () => {
    setSelectEnabled(true)
    setFormValue(template?.id)
  }

  const onCreateTemplate = () => {
    navigate('/sp-admin/templates/gallery?createnew=true')
  }

  const templatesSelectDisabled = !templatesEnabled || !selectEnabled

  return (
    <div>
      <RadioGroup className="flex flex-col gap-4">
        <Radio
          value="emptyProduct"
          defaultChecked={!selectEnabled}
          onChange={onClickEmptyProduct}
          aria-label={t('components.ui.createProductModal.radio.emptyProduct.label')}
          label={t('components.ui.createProductModal.radio.emptyProduct.label')}
          id="emptyProduct"
          name="productContent"
          size="s"
        />
        <div className="flex items-center gap-2">
          <Radio
            value="template"
            id="template"
            defaultChecked={selectEnabled}
            onChange={onClickTemplate}
            aria-label={t('components.ui.createProductModal.radio.template.label')}
            label={t('components.ui.createProductModal.radio.template.label')}
            name="productContent"
            size="s"
            disabled={!templatesEnabled}
          />
          {!templatesEnabled && (
            <UpsellCTA
              tooltip={t('entitlements.gates.default.tooltip')}
              linkHref={getUpgradeUrl(organizationRole)}
              linkText={getUpgradeLabel(organizationRole)}
            />
          )}
        </div>
      </RadioGroup>
      <span className={clsx(
        'block pl-6 mt-2 mb-1 font-medium uppercase text-body3',
        templatesSelectDisabled ? 'text-neutral-50' : 'text-neutral-80',
      )}>
        {t('components.ui.createProductModal.select.template.label')}
      </span>
      <Select
        disabled={templatesSelectDisabled}
        data-testid="create-product-select-template"
        name="template"
        className="h-8 py-0 mx-4 text-body3"
        defaultValue={template}
        placeholder={t('components.ui.createProductModal.select.template.placeholder')}
        onChange={onSelect}
        isStatic={true}
        options={templates.map((item => (
          {
            name: item.name,
            id: item.id
          })))}
        optionsClassName="w-[236px] max-h-[200px]"
        optionClassName="py-[9px]"
        topButton={{
          onClick: onCreateTemplate,
          title: <span className="flex items-center gap-2 h-[28px]">
            <IconPlusLight width={16} height={16} />
            {t('components.ui.createProductModal.select.template.topButton.label')}
          </span>
        }}
        aria-label={t('components.ui.createProductModal.select.template.ariaLabel')}
        buttonClassName="text-body2 py-1 h-8 border w-[236px]"
        size="s"
        customOptionBuilder={option => (
          <span className="flex items-center break-all whitespace-break-spaces gap-x-1">
            {defaultTemplate?.id === option.id ?
              <Tooltip
                content={t('components.ui.createProductModal.select.template.tooltip.content')}
                trigger="hover"
                defaultComp={<span className="text-[#0070E9]"><IconCircleCheck width={12} height={12} /></span>}
                triggeredComp={<span className="text-[#0070E9]"><IconCircleCheckSolid width={12} height={12} /></span>}
                placement="top"
                aria-label={t('components.ui.createProductModal.select.template.tooltip.ariaLabel')}
              /> : null}
            {option.name}
          </span>
        )}
        customButtonBuilder={name => <span className="flex items-center gap-x-1">
          {name === defaultTemplate?.name && <Tooltip
            content={t('components.ui.createProductModal.select.template.tooltip.content')}
            trigger="hover"
            defaultComp={<span className="text-[#0070E9]"><IconCircleCheck width={12} height={12} /></span>}
            triggeredComp={<span className="text-[#0070E9]"><IconCircleCheckSolid width={12} height={12} /></span>}
            placement="top"
            aria-label={t('components.ui.createProductModal.select.template.tooltip.ariaLabel')}
          />}
          {name}
        </span>}
      />
    </div>

  )
}